import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { stringifySearchTokens, useSearchStore } from '#domains/search';
import { useCurrentEditor } from '../modules/text-editor';

export const useSearchStateSync = () => {
  const navigate = useNavigate();
  const { editor } = useCurrentEditor();

  const {
    search: { q }
  } = useLocation();

  const init = useSearchStore(state => state.init);
  const reset = useSearchStore(state => state.reset);
  const searchTokens = useSearchStore(state => state.searchTokens);

  const isSyncingFromURL = useRef(false);

  // Effect 1: Never allow ugly URLs like: /search?q=
  useEffect(() => {
    if (q === '') {
      navigate({ to: '/search', search: {}, replace: true });
    }
  }, [navigate, q]);

  // Effect 2: URL → Editor
  useEffect(() => {
    if (!editor) return;

    // Mark that we're syncing from URL
    isSyncingFromURL.current = true;

    if (q) {
      init(q);
      editor.commands.setContent(q, {
        parseOptions: {
          preserveWhitespace: true
        }
      });
    } else {
      editor.commands.clearContent();
      reset();
    }
  }, [editor, init, q, reset]);

  // Effect 3: Editor → URL
  useEffect(() => {
    const searchString = stringifySearchTokens(searchTokens);

    // Check if URL → Editor sync is complete, and mark it as so
    if (searchString === q) {
      isSyncingFromURL.current = false;
    }

    // Don't update URL while a URL → Editor sync is in process
    if (isSyncingFromURL.current) {
      return;
    }

    if (searchString !== q) {
      navigate({
        to: '/search',
        search: { q: searchString }
      });
    }
  }, [navigate, q, searchTokens]);
};
